import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Drawer,
  Box,
  useTheme,
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Button,
} from "@mui/material";
import { useSnackbar } from "notistack";
import SendIcon from "@mui/icons-material/Send";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  capitalizeFirstLetter,
  extractTimeFromDateTime,
  extractTimeFromDateTime24,
  getReadableDateString,
} from "../../utils/commons";
import {
  DeleteOneNote,
  GetMemberNotes,
  SaveMemberNote,
} from "../../services/MembersService";
import { ConfirmationDialog } from "../../utils/ConfirmationDialog";

interface Note {
  id: string;
  userId: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

interface MemberNotesDrawerProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
}

const MemberNotesDrawer: React.FC<MemberNotesDrawerProps> = ({
  open,
  onClose,
  memberId,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [notes, setNotes] = useState<Note[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isFirstPage, setIsFirstPage] = useState<boolean>(true);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newNote, setNewNote] = useState<string>("");
  const [noteIdToDelete, setNoteIdToDelete] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const pageSize = 10;

  const fetchNotes = async (page: number, shouldScroll = false) => {
    setIsLoading(true);
    try {
      const data = await GetMemberNotes({ memberId, pageSize, page });
      setIsLoading(false);
      setNotes([...data.content].reverse());
      setTotalPages(data.totalPages);
      setCurrentPage(page);
      setIsFirstPage(data.first);
      setIsLastPage(data.last);
      if (shouldScroll) {
        setTimeout(scrollToBottom, 50);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
      setIsLoading(false);
      enqueueSnackbar("Failed to fetch notes", { variant: "error" });
    }
  };

  const reloadNotes = async () => {
    await fetchNotes(1, true);
  };
  useEffect(() => {
    fetchNotes(1, true);
  }, []);

  const handleAddNote = async () => {
    if (newNote.trim() === "") {
      enqueueSnackbar("Note cannot be empty", { variant: "error" });
      return;
    }
    try {
      const response = await SaveMemberNote({
        userId: memberId,
        note: capitalizeFirstLetter(newNote),
      });
      if (response) {
        reloadNotes();
      }
      setNewNote("");
    } catch (error) {
      console.error("Error adding note:", error);
      enqueueSnackbar("Failed to add note", { variant: "error" });
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    const status = await DeleteOneNote({ noteId });
    if (status === 204) {
      reloadNotes();
      enqueueSnackbar("Note deleted successfully", { variant: "success" });
    } else {
      console.error("Error deleting note:");
      enqueueSnackbar("Failed to delete note", { variant: "error" });
    }
  };

  const requestDeleteNote = (noteId: string) => {
    setNoteIdToDelete(noteId);
  };

  const handleDeleteConfirm = () => {
    if (noteIdToDelete) {
      handleDeleteNote(noteIdToDelete);
    }
  };

  const handleDeleteDialogClose = () => {
    setNoteIdToDelete(null);
  };

  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  const handleTextFieldKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddNote();
    }
  };

  const handlePrevPage = () => {
    if (currentPage < totalPages && !isLoading) {
      fetchNotes(currentPage + 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage > 1 && !isLoading) {
      fetchNotes(currentPage - 1, true);
    }
  };

  useLayoutEffect(() => {
    if (open) {
      fetchNotes(1, true);
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: theme.spacing(3),
          borderBottomLeftRadius: theme.spacing(3),
          width: 450,
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: "100vh",
          padding: theme.spacing(3),
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ marginBottom: theme.spacing(2) }}>
            Notes
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Button
              variant="outlined"
              onClick={handlePrevPage}
              disabled={currentPage >= totalPages}
              style={{ marginRight: "8px" }}
            >
              <UndoIcon />
            </Button>
            <Button
              variant="outlined"
              onClick={handleNextPage}
              disabled={currentPage <= 1}
              style={{ marginLeft: "8px" }}
            >
              <RedoIcon />
            </Button>
          </Box>
        </Box>
        <List
          ref={listRef}
          sx={{
            overflowY: "auto",
            flex: 1,
            scrollbarWidth: "none",
            marginBottom: theme.spacing(2),
          }}
        >
          {notes.map((note) => (
            <ListItem
              key={note.id}
              sx={{
                backgroundColor: theme.palette.background.default,
                marginBottom: theme.spacing(1),
                borderRadius: theme.spacing(1),
                padding: theme.spacing(2),
                boxShadow: theme.shadows[1],
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  padding: theme.spacing(2),
                  backgroundColor: theme.palette.background.default,
                  borderRadius: theme.spacing(1),
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <ListItemText primary={note.note} />
                  <Typography variant="body2" color="textSecondary">
                    <span style={{ fontSize: "inherit" }}>
                      {extractTimeFromDateTime24(note.createdAt)}
                    </span>
                    <span
                      style={{
                        color: theme.palette.secondary.main,
                        fontSize: "inherit",
                        fontWeight: "bold",
                        marginLeft: theme.spacing(1),
                        marginRight: theme.spacing(1),
                      }}
                    >
                      {" | "}
                    </span>
                    <span style={{ fontSize: "inherit" }}>
                      {getReadableDateString(note.createdAt)}
                    </span>
                    <span
                      style={{
                        color: theme.palette.secondary.main,
                        fontSize: "inherit",
                        fontWeight: "bold",
                        marginLeft: theme.spacing(1),
                      }}
                    >
                      {" | "}
                    </span>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => requestDeleteNote(note.id)}
                      sx={{ marginLeft: "auto" }}
                    >
                      <DeleteIcon
                        sx={{
                          fontSize: 15,
                          color: "gray",
                        }}
                      />
                    </IconButton>
                  </Typography>
                </div>
              </Paper>
            </ListItem>
          ))}
        </List>

        <TextField
          label="New Note"
          value={newNote}
          onChange={(e) =>
            setNewNote(
              e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1)
            )
          }
          fullWidth
          onKeyPress={handleTextFieldKeyPress}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  color="primary"
                  onClick={handleAddNote}
                  disabled={!newNote}
                  edge="end"
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ marginBottom: theme.spacing(2) }}
        />
        <ConfirmationDialog
          open={Boolean(noteIdToDelete)}
          handleClose={handleDeleteDialogClose}
          heading="Delete Note"
          description="Are you sure you want to delete this note?"
          cancelLabel="Cancel"
          confirmLabel="Delete"
          onProceed={handleDeleteConfirm}
        />
      </Box>
    </Drawer>
  );
};

export default MemberNotesDrawer;
